import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Settings, Key, Sparkles, Image, ArrowLeft, 
  Save, Eye, EyeOff, CheckCircle, AlertCircle, 
  RefreshCw, Trash2, ExternalLink, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [aiFeatures, setAiFeatures] = useState({ post_editing_enabled: false, ai_optimization_enabled: false });
  
  // API Keys state
  const [keys, setKeys] = useState({
    has_gemini_key: false,
    has_airbrush_key: false,
    gemini_key_masked: '',
    airbrush_key_masked: ''
  });
  const [geminiKey, setGeminiKey] = useState('');
  const [airbrushKey, setAirbrushKey] = useState('');
  const [showGeminiKey, setShowGeminiKey] = useState(false);
  const [showAirbrushKey, setShowAirbrushKey] = useState(false);
  const [savingKeys, setSavingKeys] = useState(false);
  const [testingGemini, setTestingGemini] = useState(false);
  const [testingAirbrush, setTestingAirbrush] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [featuresRes, keysRes] = await Promise.all([
        axios.get(`${API}/ai/features/status`).catch(() => ({ data: { post_editing_enabled: false, ai_optimization_enabled: false } })),
        axios.get(`${API}/ai/keys`, { headers }).catch(() => ({ data: {} }))
      ]);

      setAiFeatures(featuresRes.data);
      setKeys(keysRes.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveApiKeys = async () => {
    setSavingKeys(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {};
      
      // Only include keys if they were entered
      if (geminiKey.trim()) {
        payload.gemini_api_key = geminiKey.trim();
      }
      if (airbrushKey.trim()) {
        payload.airbrush_api_key = airbrushKey.trim();
      }

      if (Object.keys(payload).length === 0) {
        toast.error('Please enter at least one API key');
        return;
      }

      await axios.post(`${API}/ai/keys`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('API keys saved successfully');
      setGeminiKey('');
      setAirbrushKey('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save API keys');
    } finally {
      setSavingKeys(false);
    }
  };

  const deleteKey = async (keyType) => {
    if (!window.confirm(`Are you sure you want to remove your ${keyType === 'gemini' ? 'Gemini' : 'Airbrush'} API key?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/ai/keys/${keyType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('API key removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove API key');
    }
  };

  const testApiKey = async (keyType) => {
    const setTesting = keyType === 'gemini' ? setTestingGemini : setTestingAirbrush;
    setTesting(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ai/keys/test/${keyType}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message || 'API key is valid!');
      } else {
        toast.error(response.data.message || 'API key test failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to test API key');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Settings className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold">Settings</h1>
          </div>
        </div>

        {/* AI Features Status Banner */}
        <Card className={`mb-6 ${aiFeatures.ai_optimization_enabled ? 'bg-green-500/10 border-green-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className={`w-5 h-5 ${aiFeatures.ai_optimization_enabled ? 'text-green-400' : 'text-amber-400'}`} />
                <div>
                  <p className={`font-medium ${aiFeatures.ai_optimization_enabled ? 'text-green-400' : 'text-amber-400'}`}>
                    {aiFeatures.ai_optimization_enabled ? 'AI Optimization is enabled' : 'AI Optimization is disabled'}
                  </p>
                  <p className="text-sm text-slate-400">
                    {aiFeatures.ai_optimization_enabled 
                      ? 'You can use AI features to optimize your posts'
                      : 'Contact admin to enable AI features for your account'
                    }
                  </p>
                </div>
              </div>
              {aiFeatures.post_editing_enabled && (
                <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                  Post Editing Enabled
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* API Keys Section */}
        <Card className="bg-white/10 border-white/20 text-white mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-purple-400" />
              API Keys
            </CardTitle>
            <CardDescription className="text-slate-400">
              Configure your AI service API keys for content optimization and image generation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Gemini API Key */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-blue-400" />
                  <label className="font-medium">Google Gemini API Key</label>
                  {keys.has_gemini_key && (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                      Configured
                    </span>
                  )}
                </div>
                {keys.has_gemini_key && (
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => testApiKey('gemini')}
                      disabled={testingGemini}
                      className="text-white border-white/20"
                    >
                      {testingGemini ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Test'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => deleteKey('gemini')}
                      className="text-red-400 border-red-500/50 hover:bg-red-500/20"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
              
              {keys.has_gemini_key && (
                <div className="bg-white/5 rounded-lg px-3 py-2 font-mono text-sm text-slate-400">
                  {keys.gemini_key_masked}
                </div>
              )}
              
              <div className="relative">
                <Input
                  type={showGeminiKey ? 'text' : 'password'}
                  value={geminiKey}
                  onChange={(e) => setGeminiKey(e.target.value)}
                  placeholder={keys.has_gemini_key ? 'Enter new key to update...' : 'AIza...'}
                  className="bg-white/5 border-white/20 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowGeminiKey(!showGeminiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showGeminiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                Get your key from 
                <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline inline-flex items-center gap-1">
                  Google AI Studio <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </div>

            {/* Airbrush API Key */}
            <div className="space-y-3 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Image className="w-4 h-4 text-pink-400" />
                  <label className="font-medium">Airbrush.ai API Key</label>
                  {keys.has_airbrush_key && (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                      Configured
                    </span>
                  )}
                </div>
                {keys.has_airbrush_key && (
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => testApiKey('airbrush')}
                      disabled={testingAirbrush}
                      className="text-white border-white/20"
                    >
                      {testingAirbrush ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Test'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => deleteKey('airbrush')}
                      className="text-red-400 border-red-500/50 hover:bg-red-500/20"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
              
              {keys.has_airbrush_key && (
                <div className="bg-white/5 rounded-lg px-3 py-2 font-mono text-sm text-slate-400">
                  {keys.airbrush_key_masked}
                </div>
              )}
              
              <div className="relative">
                <Input
                  type={showAirbrushKey ? 'text' : 'password'}
                  value={airbrushKey}
                  onChange={(e) => setAirbrushKey(e.target.value)}
                  placeholder={keys.has_airbrush_key ? 'Enter new key to update...' : 'Your Airbrush API key...'}
                  className="bg-white/5 border-white/20 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowAirbrushKey(!showAirbrushKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showAirbrushKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                Get your key from 
                <a href="https://app.airbrush.ai/api-details" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline inline-flex items-center gap-1">
                  Airbrush Dashboard <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </div>

            {/* Save Button */}
            <div className="pt-4">
              <Button 
                onClick={saveApiKeys} 
                disabled={savingKeys || (!geminiKey.trim() && !airbrushKey.trim())}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {savingKeys ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save API Keys
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* What you can do section */}
        <Card className="bg-white/10 border-white/20 text-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              What you can do with AI
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg ${keys.has_gemini_key ? 'bg-green-500/10 border border-green-500/30' : 'bg-white/5'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className={`w-4 h-4 ${keys.has_gemini_key ? 'text-green-400' : 'text-slate-400'}`} />
                  <span className="font-medium">Content Optimization</span>
                  {keys.has_gemini_key ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-slate-500" />
                  )}
                </div>
                <ul className="text-sm text-slate-400 space-y-1">
                  <li>• SEO-optimized title suggestions</li>
                  <li>• Meta description generation</li>
                  <li>• Content analysis & recommendations</li>
                  <li>• Heading structure optimization</li>
                </ul>
              </div>
              
              <div className={`p-4 rounded-lg ${keys.has_airbrush_key ? 'bg-green-500/10 border border-green-500/30' : 'bg-white/5'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Image className={`w-4 h-4 ${keys.has_airbrush_key ? 'text-green-400' : 'text-slate-400'}`} />
                  <span className="font-medium">AI Image Generation</span>
                  {keys.has_airbrush_key ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-slate-500" />
                  )}
                </div>
                <ul className="text-sm text-slate-400 space-y-1">
                  <li>• Generate blog post images</li>
                  <li>• Multiple AI engines (SDXL, Midjourney)</li>
                  <li>• Various dimensions & styles</li>
                  <li>• Auto-insert into posts</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
